/**
 * Avalia se uma condição é verdadeira baseado nos dados do formulário.
 * Espelha o conditionEvaluator do form builder (lp-padrao), no estilo lint do admin.
 */
export function evaluateCondition (condition, formData, allFields = []) {
  const fieldId = condition.field_id
  const operator = condition.operator
  const value = condition.value

  let fieldValue

  if (allFields.length > 0) {
    const targetField = allFields.find(f =>
      String(f.id) === String(fieldId) ||
      String(f._tempId) === String(fieldId)
    )
    if (targetField) {
      fieldValue = formData[targetField.name]
    }
  } else {
    fieldValue = formData[fieldId]
    if (fieldValue === undefined) {
      const fieldIdStr = String(fieldId)
      const possibleKey = Object.keys(formData).find(key =>
        fieldIdStr.includes(key) || key.includes('has_companion')
      )
      if (possibleKey) {
        fieldValue = formData[possibleKey]
      }
    }
  }

  // Multi-select / checkbox group entregam fieldValue como array.
  // Cada operador trata os 2 casos (array vs string).
  const isArray = Array.isArray(fieldValue)

  switch (operator) {
    case '==':
    case 'equals':
      if (isArray) return fieldValue.some(v => String(v) === String(value))
      return String(fieldValue) === String(value)

    case '!=':
    case 'not_equals':
      if (isArray) return !fieldValue.some(v => String(v) === String(value))
      return String(fieldValue) !== String(value)

    case 'contains':
      if (isArray) return fieldValue.some(v => String(v).includes(value))
      return String(fieldValue || '').includes(value)

    case 'not_contains':
      if (isArray) return !fieldValue.some(v => String(v).includes(value))
      return !String(fieldValue || '').includes(value)

    case 'is_empty':
      if (isArray) return fieldValue.length === 0
      return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined

    case 'is_not_empty':
      if (isArray) return fieldValue.length > 0
      return !!fieldValue && fieldValue !== ''

    case '>':
    case 'greater_than':
      return Number(fieldValue) > Number(value)

    case '<':
    case 'less_than':
      return Number(fieldValue) < Number(value)

    case '>=':
    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(value)

    case '<=':
    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(value)

    case 'starts_with':
      return String(fieldValue || '').startsWith(value)

    case 'ends_with':
      return String(fieldValue || '').endsWith(value)

    case 'in': {
      // value vem como array ou CSV ("opcao1, opcao2, opcao3").
      // Para multi-select, true se QUALQUER opção marcada está na lista.
      const values = Array.isArray(value) ? value.map(String) : String(value).split(',').map(v => v.trim())
      if (isArray) return fieldValue.some(v => values.includes(String(v)))
      return values.includes(String(fieldValue))
    }

    case 'not_in': {
      const notInValues = Array.isArray(value) ? value.map(String) : String(value).split(',').map(v => v.trim())
      if (isArray) return !fieldValue.some(v => notInValues.includes(String(v)))
      return !notInValues.includes(String(fieldValue))
    }

    default:
      return true
  }
}

/**
 * Avalia múltiplas condições com lógica AND/OR.
 */
export function evaluateConditions (conditions, logic, formData, allFields = []) {
  if (!conditions || conditions.length === 0) return true

  if (logic === 'or') {
    return conditions.some(cond => evaluateCondition(cond, formData, allFields))
  }
  return conditions.every(cond => evaluateCondition(cond, formData, allFields))
}

/**
 * Verifica se um campo deve ser exibido baseado em suas condições.
 */
export function shouldShowField (field, formData, allFields = []) {
  if (!field) return false
  if (!field.conditions || field.conditions.length === 0) {
    return true
  }

  if (Array.isArray(field.conditions) && field.conditions[0] && field.conditions[0].rules) {
    const condition = field.conditions[0]
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields)
  }

  return evaluateConditions(field.conditions, field.condition_logic || 'and', formData, allFields)
}

/**
 * Verifica se um step deve ser exibido baseado em suas condições.
 */
export function shouldShowStep (step, formData, allFields = []) {
  if (!step) return false
  if (!step.conditions || step.conditions.length === 0) {
    return true
  }

  if (Array.isArray(step.conditions) && step.conditions[0] && step.conditions[0].rules) {
    const condition = step.conditions[0]
    return evaluateConditions(condition.rules, condition.logic || 'and', formData, allFields)
  }

  return evaluateConditions(step.conditions, step.condition_logic || 'and', formData, allFields)
}

/**
 * Filtra apenas os campos visíveis baseado nas condições.
 */
export function getVisibleFields (fields, formData, allFields = []) {
  return fields.filter(field => shouldShowField(field, formData, allFields))
}

/**
 * Retorna todos os campos obrigatórios visíveis.
 */
export function getRequiredVisibleFields (fields, formData, allFields = []) {
  return getVisibleFields(fields, formData, allFields).filter(field => field.required)
}
